import {useEffect, useRef, useState} from 'react';
import useMessages from 'hooks/useMessages.hook';
import TOAST from 'consts/toast.consts';
import NeoButtonMain from 'design/design_components/neo/button/NeoButtonMain.base';
import LoadingDialog from 'components/LoadingDialog.component';
import CampaignsService from 'services/campaigns.service';
import numberUtil from 'utils/number.util';
import { on } from 'infrastructure/eventHub';

export default function CampaignsMassiveFileDownloadButton(props) {
  const messages = useMessages();
  const activeRequestIdRef = useRef();
  const [showDownloadingDialog, setShowDownloadingDialog] = useState(false);
  const [loadingDialogText, setLoadingDialogText] = useState('Generando archivo (.zip)');
  const [loadingDialogProgress, setLoadingDialogProgress] = useState(0);

  const clearActiveRequest = () => {
    activeRequestIdRef.current = undefined;
  };

  const closeProgressDialog = () => {
    setShowDownloadingDialog(false);
    clearActiveRequest();
  };

  const getCampaignLabel = (campaign) => {
    if(campaign === undefined || campaign === null) {
      return '';
    }
    if(typeof campaign === 'string' || typeof campaign === 'number') {
      return `${campaign}`;
    }
    return campaign.name ?? campaign.id ?? '';
  };

  const getProgressLabel = (processedCampaigns, totalCampaigns) => {
    if(typeof processedCampaigns !== 'number' || typeof totalCampaigns !== 'number') {
      return '';
    }
    return ` (${numberUtil.format(processedCampaigns)}/${numberUtil.format(totalCampaigns)})`;
  };

  const triggerDownload = (urlDownload) => {
    const downloadInstance = document.createElement('a');
    downloadInstance.href = urlDownload;
    downloadInstance.target = '_blank';
    downloadInstance.click();
  };

  useEffect(() => {
    const unsub = on("campaign", "massiveFile", "progress", ({ payload }) => {
      const activeRequestId = activeRequestIdRef.current?.toString();
      const eventRequestId = payload?.requestId?.toString();
      if(!activeRequestId || !eventRequestId || eventRequestId !== activeRequestId) {
        return;
      }

      const {
        status,
        progress,
        totalCampaigns = 0,
        processedCampaigns = 0,
        campaign,
        urlDownload,
        error
      } = payload;

      if(typeof progress === 'number') {
        setLoadingDialogProgress(progress);
      }

      const campaignLabel = getCampaignLabel(campaign);
      const progressLabel = getProgressLabel(processedCampaigns, totalCampaigns);

      switch(status) {
        case 'GETTING_CAMPAIGNS':
          setLoadingDialogText('Obteniendo campañas...');
          break;
        case 'PROCESSING_CAMPAIGNS':
          setLoadingDialogText(`Procesando campañas${progressLabel}${campaignLabel ? ` · ${campaignLabel}` : ''}...`);
          break;
        case 'UPLOADING_FILE':
          setLoadingDialogText(`Subiendo archivo${progressLabel}...`);
          break;
        case 'ZIPPING_FILES':
          setLoadingDialogText(`Comprimiendo archivos (.zip)${progressLabel}...`);
          break;
        case 'ASSIGNING_PERMISSIONS':
          setLoadingDialogText(`Configurando permisos para descarga${progressLabel}...`);
          break;
        case 'COMPLETE':
          setLoadingDialogText(`Archivo generado${progressLabel}.`);
          if(urlDownload) {
            triggerDownload(urlDownload);
            messages.showToast(TOAST.SEVERITY.SUCCESS, 'Operación exitosa', `Se descargó el reporte masivo${progressLabel}.`);
          }
          else {
            messages.showToast(TOAST.SEVERITY.ERROR, 'Algo salió mal', 'El archivo se generó, pero no se recibió la URL de descarga.');
          }
          closeProgressDialog();
          break;
        case 'FAILED':
          setLoadingDialogText(`No se pudo generar el archivo${progressLabel}.`);
          messages.showToast(
            TOAST.SEVERITY.ERROR,
            'Algo salió mal',
            typeof error === 'string' ? error : (error?.message ?? 'No se pudo generar el reporte masivo de campañas, inténtalo de nuevo.')
          );
          closeProgressDialog();
          break;
        default:
          break;
      }
    });

    return () => {
      unsub();
      clearActiveRequest();
    };
  }, []);

  const handleDownloadButtonClick = async () => {
    const linesId = props.linesId;
    const fromDateString = props.period?.from;
    const toDateString = props.period?.to;
    if(linesId?.length > 0 && fromDateString !== undefined && toDateString !== undefined) {
      setShowDownloadingDialog(true);
      setLoadingDialogText('Iniciando proceso...');
      setLoadingDialogProgress(0);
      const response = await CampaignsService.getCampaignsMessagesMassiveFile(linesId, fromDateString, toDateString);
      if(response.success) {
        const requestId = response.payload?.requestId;
        if(requestId) {
          activeRequestIdRef.current = requestId.toString();
          setLoadingDialogText('Solicitud enviada. Esperando progreso...');
        }
        else {
          messages.showToast(TOAST.SEVERITY.ERROR, 'Algo salió mal', 'No se recibió el identificador de la solicitud para seguimiento.');
          closeProgressDialog();
        }
      }
      else {
        messages.showToast(TOAST.SEVERITY.ERROR, 'Algo salió mal', 'No se pudo iniciar la generación del reporte masivo, inténtalo de nuevo.');
        closeProgressDialog();
      }
    }
  };

  const isDisabled = showDownloadingDialog || props.loading || !props.linesId?.length || !props.period?.from || !props.period?.to;

  return (
    <>
      <NeoButtonMain
        icon='pi pi-download'
        label='Reporte masivo'
        disabled={isDisabled}
        onClick={handleDownloadButtonClick}
      />
      {
        <LoadingDialog
          visible={showDownloadingDialog}
          message={loadingDialogText}
          progress={loadingDialogProgress}
          showValue={false}
        />
      }
    </>
  );
}
